import type { AllChartsDataMap } from '../modules/analytic/types';
import type { IPAColumnTypes } from '../types/page-analytic-v2';
import type { RawMetricRow } from '../helpers/pageDetailV2';

import {
  DEMO_COLUMN_TYPES,
  DEMO_COMPARISON_TOTALS_ROW,
  DEMO_DATE_FILTER_ALIAS,
  DEMO_TOTALS_ROW,
} from '../constants/pageAnalyticDemo';
import { CompareDateTimePickerAlias } from '~/presentation/ui';
import { buildDemoChartsDataMap, getDemoPeriods } from '../helpers/pageAnalyticDemo';
import { getPeriodChartName } from '../modules/analytic/helpers/chart';
import { createContext, useContext, useMemo } from 'react';

export type IPageAnalyticDemoContext = {
  totalsRow: RawMetricRow;
  comparisonTotalsRow: RawMetricRow;
  columnTypes: IPAColumnTypes;
  currentPeriodChartName: string;
  previousPeriodChartName: string;
  allChartsDataMap: AllChartsDataMap;
};

type IProps = {
  children: React.ReactNode;
};

const PageAnalyticDemoContext = createContext<IPageAnalyticDemoContext | undefined>(undefined);

/** The sample metrics every SDK card on the demo page draws from, on the sample data's dates. */
export const PageAnalyticDemoProvider = ({ children }: IProps) => {
  const contextValue = useMemo<IPageAnalyticDemoContext>(() => {
    const { current, previous } = getDemoPeriods();

    return {
      totalsRow: DEMO_TOTALS_ROW,
      comparisonTotalsRow: DEMO_COMPARISON_TOTALS_ROW,
      columnTypes: DEMO_COLUMN_TYPES,
      currentPeriodChartName: getPeriodChartName({
        ...current,
        alias: DEMO_DATE_FILTER_ALIAS,
      }),
      previousPeriodChartName: getPeriodChartName({
        ...previous,
        alias: CompareDateTimePickerAlias.PREVIOUS_PERIOD,
      }),
      allChartsDataMap: buildDemoChartsDataMap({ current, previous }),
    };
  }, []);

  return (
    <PageAnalyticDemoContext.Provider value={contextValue}>
      {children}
    </PageAnalyticDemoContext.Provider>
  );
};

export type IUsePageAnalyticDemoContextResult = IPageAnalyticDemoContext;

export const usePageAnalyticDemoContext = (): IUsePageAnalyticDemoContextResult => {
  const context = useContext(PageAnalyticDemoContext);
  if (!context) {
    throw new Error('usePageAnalyticDemoContext must be used within a PageAnalyticDemoProvider');
  }
  return context;
};
